import { Button } from "@blurb/codex-react";
import { Group, MantineProvider, Menu } from "@mantine/core";
import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { IconV3, useShowSuccessToast } from "@rpi/openapi-core";
import React from "react";
import { Outlet, useLocation, useNavigate, useOutlet } from "react-router-dom";

import { ConfirmationModalV2 } from "../../../components/modals/ConfirmationModalV2";
import { Pagination } from "../../../components/Pagination";
import { RpaTableMenu, createRpaTableMenuColumn } from "../../../components/RpaTableMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { openApiProductColumns } from "../../../configuration/columns/openApiProduct.column";
import { openApiProductFilters } from "../../../configuration/filters/openApiProduct.filter";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";
import { OpenApiProductCreateView, OpenApiProductEditView } from "./OpenApiProductView";

export const OpenApiProductsView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.OpenApiProductsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.OpenApiProductsQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.OpenApiProductsQueryVariables>>();
  const [isSyncModalOpen, setIsSyncModalOpen] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | undefined>();

  const pagination = useRpiPagination({ perPage: 20 });

  const showSuccessToast = useShowSuccessToast();
  const { mutateAsync: syncStandardProducts, ...syncMutation } = GraphQL.useAdminSyncStandardProductsMutation();

  const { data, isLoading, error, refetch } = GraphQL.useOpenApiProductsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.openapiProducts.totalCount),
    }
  );
  const openApiProducts = data?.openapiProducts.data;

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  const handleSyncSkus = React.useCallback(async () => {
    setSyncError(undefined);
    try {
      await syncStandardProducts({});
      showSuccessToast("All SKU data has been synced from the source of truth.");
      setIsSyncModalOpen(false);
      refetch();
    } catch (error: any) {
      setSyncError("An error occurred while syncing SKUs.");
    }
  }, [syncStandardProducts, refetch, showSuccessToast]);

  const location = useLocation();
  const isCreateOpen = location.pathname.includes(`/create`);
  const isEditOpen = location.pathname.includes(`/edit/`);
  const navigate = useNavigate();

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="catalog-view" aria-label="Catalog view">
        <Heading as="h1" size="6xl">
          Catalog
        </Heading>
        <div className={classes.row}>
          <RpiSearchFilter
            placeholder="Search by Name"
            filters={openApiProductFilters}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
          <Group spacing="xs">
            <Button
              variant="outlined"
              intent="primary"
              onClick={() => setIsSyncModalOpen(true)}
              data-testid="syncSkusButton"
            >
              <Group spacing="xs">
                <IconV3 icon="refresh" color="inherit" /> Sync SKUs
              </Group>
            </Button>
            <Button variant="filled" intent="primary" onClick={() => navigate("create")} data-testid="addNewSkuButton">
              <Group spacing="xs">
                <IconV3 icon="add" color="inherit" /> Add New SKU
              </Group>
            </Button>
          </Group>
        </div>
        <RpiTableContainer
          data-testid="open-api-products-table"
          footer={<Pagination isLoading={isLoading} pagination={pagination} />}
        >
          <RpiTable
            emptyMessage={searchInputs?.length || searchGeneral ? "No products found." : "Products will appear here."}
            isLoading={isLoading}
            error={error}
            perPage={openApiProducts?.length}
            data={openApiProducts}
            columns={openApiProductColumns}
            additionalColumns={[
              createRpaTableMenuColumn<GraphQLPagedQueryData<GraphQL.OpenApiProductsQuery>>((row) => (
                <OpenApiProductsActionMenu skuId={row.id} />
              )),
            ]}
            withSortBy={{ onChange: setSortInput }}
          />
        </RpiTableContainer>
      </div>

      <OpenApiProductCreateView isOpened={isCreateOpen} onClose={() => navigate("./")}>
        <Outlet />
      </OpenApiProductCreateView>
      <OpenApiProductEditView isOpened={isEditOpen} onClose={() => navigate("./")}>
        <Outlet />
      </OpenApiProductEditView>
      {!isCreateOpen && !isEditOpen && outlet}

      <ConfirmationModalV2
        opened={isSyncModalOpen}
        onClose={() => {
          setIsSyncModalOpen(false);
          setSyncError(undefined);
        }}
        onConfirm={handleSyncSkus}
        isLoading={syncMutation.isLoading}
        error={syncError}
        title="Sync SKUs from Source of Truth"
        message="This will sync the product offering's source of truth with the database. All SKU data in the source of truth will overwrite the matching SKUs in the database. Do you wish to proceed?"
        loadingLabel="Syncing..."
      />
    </MantineProvider>
  );
};

type OpenApiProductsActionMenuProps = {
  skuId: number;
};

function OpenApiProductsActionMenu({ skuId }: OpenApiProductsActionMenuProps) {
  const navigateRoute = useNavigateRoute();
  const navigate = useNavigate();
  return (
    <RpaTableMenu>
      <Menu.Item
        icon={<IconV3 icon="description" size={20} />}
        onClick={() => navigateRoute(["open-api-product", { id: skuId }])}
      >
        View Details
      </Menu.Item>
      <Menu.Item icon={<IconV3 icon="edit" size={20} />} onClick={() => navigate(`edit/${skuId}`)}>
        Edit SKU
      </Menu.Item>
    </RpaTableMenu>
  );
}
